import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import HeaderCard from "components/DataTable/HeaderCard";
import Loading from "components/Loading";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { removeEmptyFields } from "utils/helper";
import { parentRoute } from ".";
// You need to implement these API functions for vendor
import { yupResolver } from "@hookform/resolvers/yup";
import { Checkbox, FormControlLabel } from "@mui/material";
import { getVendorCategories, useCreateVendor, useDeleteVendor, useUpdateVendor, useVendorDetails } from "api/vendor";
import CustomReactSelect from "components/CustomReactSelect";
import ImageUploadField from "components/ImageUploadField";
import MDTypography from "components/MDTypography";
import { useForm } from "react-hook-form";
import useGlobalStore from "store/useGlobalStore";
import { jsonToFormData } from "utils/helper";
import * as yup from "yup";
import { formatDateTime } from "utils/helper";

// Simple debounce function
const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
};

const vendorSchema = yup.object({
    name: yup.string().trim().required("Vendor name is required").max(100, "Max 100 characters"),
    description: yup.string().max(200, "Max 200 characters").notRequired(),
    url: yup.string().url("URL must be valid").nullable().notRequired(),
    category: yup.string().nullable().notRequired(),
    hourly_rate: yup
        .number()
        .typeError("Hourly rate must be a number")
        .min(0, "Hourly rate cannot be negative")
        .nullable()
        .transform((v, o) => (o === "" || o === null ? null : v)),
    address: yup.string().max(250, "Max 250 characters").notRequired(),
    contacts: yup.array().of(
        yup.object({
            name: yup.string().required("Contact name is required"),
            email: yup.string().email("Invalid email").nullable(),
            phone: yup.string().nullable(),
            position: yup.string().nullable(),
            is_primary: yup.boolean(),
        })
    ),
});

const AddVendor = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { vendorCategories, setVendorCategories, setPrevSelectedRow } = useGlobalStore();
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        url: "",
        category: "",
        hourly_rate: "",
        address: "",
        is_parts_supplier: false,
        contacts: [],
        image: null,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const id = Number(location.pathname.split('/')[2]);

    const isAdd = location.pathname.includes(`${parentRoute}/add-vendor`);
    const vendorQuery = useVendorDetails(id, { enabled: !isAdd });
    const vendorData = vendorQuery.data;
    const isDetailsLoading = !isAdd && vendorQuery.isLoading;

    const {
        trigger,
        setValue,
        formState: { errors: rhfErrors },
    } = useForm({
        resolver: yupResolver(vendorSchema),
        mode: "onSubmit",
        defaultValues: {
            name: "",
            description: "",
            url: "",
            category: "",
            hourly_rate: "",
            address: "",
            is_parts_supplier: false,
            contacts: [],
            image: null,
        },
    });

    // Sync your formData to RHF for validation
    useEffect(() => {
        Object.entries(formData).forEach(([k, v]) => setValue(k, v, { shouldValidate: false, shouldDirty: false }));
    }, [formData, setValue]);

    // Map RHF errors to your formErrors for UI
    useEffect(() => {
        const mapped = {};
        Object.entries(rhfErrors || {}).forEach(([k, v]) => {
            if (Array.isArray(v)) {
                // For array fields like contacts
                mapped[k] = v.map(item => {
                    if (item?.message) return item.message; // single error
                    if (typeof item === 'object') {
                        const obj = {};
                        Object.entries(item).forEach(([subKey, subVal]) => {
                            obj[subKey] = subVal?.message || "";
                        });
                        return obj;
                    }
                    return "";
                });
            } else {
                mapped[k] = v?.message || "";
            }
        });
        setFormErrors(mapped);
    }, [rhfErrors]);

    useEffect(() => {
        const loadData = async () => {
            if (isAdd) {
                // Load initial categories for new vendor
                await handleCategoriesFetch('');
                return;
            }

            if (!id || isNaN(id)) {
                navigate(parentRoute, { state: location.state });
                return;
            }

            if (vendorQuery.isError) {
                toast.error("Failed to find vendor data");
                navigate(parentRoute, { state: location.state });
                return;
            }

            if (vendorData) {
                setFormData({
                    id: vendorData.id,
                    name: vendorData.name,
                    description: vendorData.description,
                    url: vendorData.url,
                    category: vendorData.category,
                    hourly_rate: vendorData.hourly_rate,
                    address: vendorData.address,
                    is_parts_supplier: vendorData.is_parts_supplier,
                    contacts: vendorData.contacts?.map(contact => ({
                        name: contact.name,
                        email: contact.email,
                        phone: contact.phone,
                        position: contact.position,
                        is_primary: contact.is_primary,
                    })) || [],
                    image: vendorData.img_url,
                });
                await handleCategoriesFetch('');
            }
        };

        loadData();
    }, [isAdd, id, location.state, navigate, vendorData, vendorQuery.isError]);

    const handleChange = useCallback((e) => {
        const { name, value, type, checked, files } = e.target;
        if (type === "checkbox") {
            setFormData((prev) => ({ ...prev, [name]: checked }));
        } else if (type === "file") {
            setFormData((prev) => ({ ...prev, [name]: files[0] }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    }, []);

    const createVendorMutation = useCreateVendor();
    const updateVendorMutation = useUpdateVendor();
    const deleteVendorMutation = useDeleteVendor();

    const handleDelete = async () => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes",
        });

        if (result.isConfirmed) {
            await deleteVendorMutation.mutateAsync(formData.id);
            toast.success("Vendor deleted successfully");
            navigate(parentRoute);
        }
    };

    const handleSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            setFormErrors({});
            // Validation
            try {
                await vendorSchema.validate(formData, { abortEarly: false });
            } catch (validationError) {
                const newErrors = {};
                validationError.inner.forEach(err => {
                    newErrors[err.path] = err.message;
                });
                setFormErrors(newErrors);
                return;
            }

            setIsLoading(true);
            try {
                let passData = jsonToFormData(removeEmptyFields(formData))
                if (isAdd) {
                    await createVendorMutation.mutateAsync(passData);
                    toast.success("Vendor added successfully");
                    navigate(parentRoute, { state: location.state });
                } else {
                    await updateVendorMutation.mutateAsync({ id: formData.id, vendorData: passData });
                    toast.success("Vendor updated successfully");
                }
            } catch (err) {
                setFormErrors(err.data?.errors || {});
                toast.error(
                    err?.data?.message || (isAdd ? "Failed to create vendor" : "Failed to update vendor")
                );
            } finally {
                if (!vendorCategories.find(category => category === formData.category)) {
                    setVendorCategories([]);
                }
                setIsLoading(false);
            }
        },
        [formData, navigate, isAdd, location.state, createVendorMutation, updateVendorMutation, setVendorCategories, vendorCategories]
    );

    const handleValidatedSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            setFormErrors({});
            const valid = await trigger();
            if (!valid) {
                toast.error("Please fix the validation errors before submitting.");
                return;
            }
            await handleSubmit(e); // your original submit logic
        },
        [trigger, handleSubmit]
    );

    const [categoryOptions, setCategoryOptions] = useState([]);

    const handleCategoriesFetch = async (inputValue) => {
        try {
            const response = await getVendorCategories({ search: inputValue || '' });
            const data = response.data?.data ?? [];
            const options = data.map(item => ({
                label: item,
                value: item
            }));
            setCategoryOptions(options);
            return options;
        } catch (error) {
            console.error('Error fetching categories:', error);
            return [];
        }
    };

    // Create a debounced version of the fetch function
    const debouncedFetchCategories = useRef(
        debounce(async (inputValue, callback) => {
            const options = await handleCategoriesFetch(inputValue);
            callback(options);
        }, 300)
    ).current;

    const loadCategoryOptions = useCallback((inputValue, callback) => {
        // If no input, return cached options immediately
        if (!inputValue || inputValue.trim() === '') {
            callback(categoryOptions);
            return;
        }

        // Use debounced function for API calls
        debouncedFetchCategories(inputValue, callback);
    }, [categoryOptions, debouncedFetchCategories]);

    const getCategoryValue = () => {
        if (!formData.category) return null;
        return {
            label: formData.category,
            value: formData.category
        };
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={4} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <HeaderCard
                                title={isAdd ? "Create a New Vendor" : "Edit Vendor"}
                                showBackButton={true}
                                onBackClick={() => { navigate(parentRoute, { state: location.state }); setPrevSelectedRow(id, parentRoute) }}
                            />
                            {isDetailsLoading ? (
                                <Loading />
                            ) : (
                                <MDBox pt={3} px={2} pb={2}>
                                    {!isAdd && (
                                        <MDBox mb={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                                            <MDTypography variant="body2" color="text" gutterBottom>
                                                Created At: {vendorData?.created_at ? formatDateTime(vendorData.created_at) : '-'}
                                            </MDTypography>
                                            <MDTypography variant="body2" color="text" gutterBottom>
                                                Last Updated: {vendorData?.updated_at ? formatDateTime(vendorData.updated_at) : '-'}
                                            </MDTypography>
                                        </MDBox>
                                    )}
                                    <form noValidate onSubmit={handleValidatedSubmit}>
                                        <Grid item xs={12}>
                                            <Grid container spacing={3}>
                                                <Grid item xs={12}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Vendor name"
                                                        name="name"
                                                        value={formData.name}
                                                        onChange={handleChange}
                                                        required
                                                        helperText={formErrors.name}
                                                        error={Boolean(formErrors.name)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Description"
                                                        name="description"
                                                        value={formData.description}
                                                        onChange={handleChange}
                                                        inputProps={{ maxLength: 200 }}
                                                        helperText={formErrors.description}
                                                        error={Boolean(formErrors.description)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <MDInput
                                                        fullWidth
                                                        label="URL"
                                                        name="url"
                                                        placeholder="https://example.com"
                                                        value={formData.url}
                                                        onChange={handleChange}
                                                        inputProps={{ maxLength: 200 }}
                                                        helperText={formErrors.url}
                                                        error={Boolean(formErrors.url)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <CustomReactSelect
                                                        async
                                                        creatable
                                                        label="Category"
                                                        isSearchable
                                                        isClearable
                                                        loadOptions={loadCategoryOptions}
                                                        defaultOptions={categoryOptions}
                                                        value={getCategoryValue()}
                                                        onChange={(option) => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                category: option ? option.value : ''
                                                            }));
                                                        }}
                                                        placeholder="Select category or type to create new one"
                                                        noOptionsMessage={() => "Start typing to create a category"}
                                                        formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
                                                        isValidNewOption={(inputValue) => inputValue && inputValue.trim().length > 0}
                                                        helperText={formErrors.category}
                                                        error={Boolean(formErrors.category)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Hourly Rate"
                                                        name="hourly_rate"
                                                        type="number"
                                                        value={formData.hourly_rate}
                                                        onChange={handleChange}
                                                        helperText={formErrors.hourly_rate}
                                                        error={Boolean(formErrors.hourly_rate)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                name="is_parts_supplier"
                                                                checked={formData.is_parts_supplier}
                                                                onChange={handleChange}
                                                            />
                                                        }
                                                        label="Is Parts Supplier"
                                                        helperText={formErrors.is_parts_supplier}
                                                        error={Boolean(formErrors.is_parts_supplier)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Address"
                                                        name="address"
                                                        value={formData.address}
                                                        onChange={handleChange}
                                                        inputProps={{ maxLength: 250 }}
                                                        helperText={formErrors.address}
                                                        error={Boolean(formErrors.address)}
                                                    />
                                                </Grid>

                                                {/* Contacts Section */}
                                                <Grid item xs={12}>
                                                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                                        <MDTypography variant="h6">Contacts</MDTypography>
                                                        <MDButton
                                                            variant="outlined"
                                                            color="primary"
                                                            size="small"
                                                            onClick={() => {
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    contacts: [...(prev.contacts || []), {
                                                                        name: "",
                                                                        email: "",
                                                                        phone: "",
                                                                        position: "",
                                                                        is_primary: false
                                                                    }]
                                                                }));
                                                            }}
                                                        >
                                                            Add Contact
                                                        </MDButton>
                                                    </MDBox>
                                                    {(formData.contacts || []).map((contact, index) => (
                                                        <Card key={index} variant="outlined" sx={{ mb: 2, p: 2 }} >
                                                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                                                <MDTypography variant="subtitle1">Contact {index + 1}</MDTypography>
                                                                <MDButton
                                                                    variant="text"
                                                                    color="error"
                                                                    size="small"
                                                                    onClick={() => {
                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            contacts: prev.contacts.filter((_, i) => i !== index)
                                                                        }));
                                                                    }}
                                                                >
                                                                    Remove
                                                                </MDButton>
                                                            </MDBox>
                                                            <Grid container spacing={2}>
                                                                <Grid item xs={12} md={6}>
                                                                    <MDInput
                                                                        fullWidth
                                                                        required
                                                                        label="Contact Name"
                                                                        value={contact.name}
                                                                        onChange={(e) => {
                                                                            const newContacts = [...formData.contacts];
                                                                            newContacts[index].name = e.target.value;
                                                                            setFormData(prev => ({ ...prev, contacts: newContacts }));
                                                                        }}
                                                                        helperText={formErrors.contacts?.[index]?.name}
                                                                        error={Boolean(formErrors.contacts?.[index]?.name)}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={6}>
                                                                    <MDInput
                                                                        fullWidth
                                                                        label="Position/Title"
                                                                        value={contact.position}
                                                                        onChange={(e) => {
                                                                            const newContacts = [...formData.contacts];
                                                                            newContacts[index].position = e.target.value;
                                                                            setFormData(prev => ({ ...prev, contacts: newContacts }));
                                                                        }}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={6}>
                                                                    <MDInput
                                                                        fullWidth
                                                                        label="Contact Email"
                                                                        type="email"
                                                                        value={contact.email}
                                                                        onChange={(e) => {
                                                                            const newContacts = [...formData.contacts];
                                                                            newContacts[index].email = e.target.value;
                                                                            setFormData(prev => ({ ...prev, contacts: newContacts }));
                                                                        }}
                                                                        helperText={formErrors.contacts?.[index]?.email}
                                                                        error={Boolean(formErrors.contacts?.[index]?.email)}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={6}>
                                                                    <MDInput
                                                                        fullWidth
                                                                        label="Contact Phone"
                                                                        inputProps={{ maxLength: 50 }}
                                                                        value={contact.phone}
                                                                        onChange={(e) => {
                                                                            const newContacts = [...formData.contacts];
                                                                            newContacts[index].phone = e.target.value;
                                                                            setFormData(prev => ({ ...prev, contacts: newContacts }));
                                                                        }}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={contact.is_primary}
                                                                                onChange={(e) => {
                                                                                    const newContacts = [...formData.contacts];
                                                                                    // If setting as primary, unset all others
                                                                                    if (e.target.checked) {
                                                                                        newContacts.forEach((c, i) => {
                                                                                            c.is_primary = i === index;
                                                                                        });
                                                                                    } else {
                                                                                        newContacts[index].is_primary = false;
                                                                                    }
                                                                                    setFormData(prev => ({ ...prev, contacts: newContacts }));
                                                                                }}
                                                                            />
                                                                        }
                                                                        label="Primary Contact"
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        </Card>
                                                    ))}
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <ImageUploadField
                                                        value={formData.image}
                                                        onChange={(file) => setFormData(prev => ({ ...prev, image: file }))}
                                                        name="image"
                                                        label="Vendor Image/Logo"
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Grid container>
                                                        <Grid item>
                                                            {!isAdd && (
                                                                <MDButton
                                                                    variant="contained"
                                                                    color="error"
                                                                    onClick={handleDelete}
                                                                    loading={isLoading}
                                                                >
                                                                    Delete
                                                                </MDButton>
                                                            )}
                                                        </Grid>
                                                        <Grid item xs />
                                                        <Grid item>
                                                            <MDBox display="flex" justifyContent="flex-end">
                                                                <MDButton
                                                                    variant="contained"
                                                                    color="primary"
                                                                    type="submit"
                                                                    loading={isLoading}
                                                                >
                                                                    Submit
                                                                </MDButton>
                                                            </MDBox>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </form>
                                </MDBox>
                            )}
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
        </DashboardLayout>
    );
};

export default AddVendor;
